'use client'

import { ninxAuthService } from './ninx-auth'
import { hasValidNinxSession, getNinxKeys } from './auth-cookies'

/**
 * Session Refresh Manager
 * Handles automatic session refresh before 30-minute expiry
 * Keeps users logged in seamlessly by refreshing NINX sessions proactively
 */
const DEFAULT_SESSION_DURATION_MS = 30 * 60 * 1000
const REFRESH_LEAD_TIME_MS = 5 * 60 * 1000

export class SessionRefreshManager {
  private static instance: SessionRefreshManager
  private refreshTimer: NodeJS.Timeout | null = null
  private isRefreshing = false
  private sessionExpiryTime: number | null = null
  
  private constructor() {}

  public static getInstance(): SessionRefreshManager {
    if (!SessionRefreshManager.instance) {
      SessionRefreshManager.instance = new SessionRefreshManager()
    }
    return SessionRefreshManager.instance
  }

  /**
   * Start session refresh monitoring
   * Call this after successful login
   */
  public startSessionRefresh(sessionExpiresIn?: number): void {
    console.log('🔄 Starting session refresh monitoring...')

    const expiryDuration =
      typeof sessionExpiresIn === 'number' && !Number.isNaN(sessionExpiresIn)
        ? sessionExpiresIn
        : DEFAULT_SESSION_DURATION_MS

    this.sessionExpiryTime = Date.now() + expiryDuration

    if (typeof window !== 'undefined') {
      localStorage.setItem(
        'ninx_session_expiry',
        this.sessionExpiryTime.toString()
      )
    }

    this.scheduleRefresh()
  }

  /**
   * Resume session refresh monitoring after page reload
   * Call this on app initialization if user is already logged in
   */
  public resumeSessionRefresh(): void {
    if (typeof window === 'undefined') return

    const storedExpiry = localStorage.getItem('ninx_session_expiry')
    if (!storedExpiry) return

    this.sessionExpiryTime = parseInt(storedExpiry, 10)
    const timeUntilExpiry = this.sessionExpiryTime - Date.now()

    // If session is already expired or expiring soon, refresh immediately
    if (timeUntilExpiry <= REFRESH_LEAD_TIME_MS) {
      console.log('📱 Session expiring soon, refreshing immediately...')
      this.performRefresh()
    } else {
      console.log('🔄 Resuming session refresh monitoring...')
      this.scheduleRefresh()
    }
  }

  /**
   * Schedule the next refresh
   */
  private scheduleRefresh(): void {
    if (!this.sessionExpiryTime) return

    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
    }

    const timeUntilRefresh =
      this.sessionExpiryTime - Date.now() - REFRESH_LEAD_TIME_MS

    // Don't schedule if time has already passed
    if (timeUntilRefresh <= 0) {
      this.performRefresh()
      return
    }

    console.log(
      `⏰ Next session refresh scheduled in ${Math.round(
        timeUntilRefresh / 1000 / 60
      )} minutes`
    )

    this.refreshTimer = setTimeout(() => {
      this.performRefresh()
    }, timeUntilRefresh)
  }

  /**
   * Perform the actual session refresh
   */
  private async performRefresh(): Promise<void> {
    if (this.isRefreshing) {
      console.log('🔄 Session refresh already in progress...')
      return
    }

    // Check if user still has a valid session before refreshing
    if (!hasValidNinxSession()) {
      console.log('❌ No valid session found, stopping refresh monitoring')
      this.stopSessionRefresh()
      return
    }

    this.isRefreshing = true
    console.log('🔄 Performing proactive session refresh...')

    try {
      // Use the dedicated refresh endpoint instead of re-login
      const result = await ninxAuthService.refreshSession()

      if (result && result.status) {
        // Session refreshed successfully
        console.log('✅ Session refreshed successfully')

        // If the refresh response doesn't include NINX keys, try to restore them
        if (!result.ninx_keys) {
          console.log('🔄 Refresh response missing NINX keys, ensuring they are available...')
          // The keys should already be stored, but let's verify they're accessible
          const { apiKey: storedApiKey, apiSecret: storedApiSecret } = getNinxKeys()
          if (!storedApiKey || !storedApiSecret) {
            console.log('⚠️ NINX keys not found in storage after refresh, they may need to be restored')
          } else {
            console.log('✅ NINX keys confirmed available in storage')
          }
        }

        // Update expiry time for next refresh
        const newExpiryDuration =
          typeof result.data?.expiresIn === 'number' &&
          !Number.isNaN(result.data.expiresIn)
            ? result.data.expiresIn
            : DEFAULT_SESSION_DURATION_MS
        this.sessionExpiryTime = Date.now() + newExpiryDuration

        // Update localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem(
            'ninx_session_expiry',
            this.sessionExpiryTime.toString()
          )
        }

        // Schedule next refresh
        this.scheduleRefresh()

        // Trigger auth status update for UI
        if (
          typeof window !== 'undefined' &&
          (window as any).updateFullAuthStatus
        ) {
          ;(window as any).updateFullAuthStatus()
        }
      } else {
        console.error('❌ Session refresh failed - user may need to re-login')
        this.stopSessionRefresh()

        // Optionally trigger a re-login flow
        this.handleRefreshFailure()
      }
    } catch (error) {
      console.error('❌ Session refresh error:', error)
      this.stopSessionRefresh()
      this.handleRefreshFailure()
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Handle refresh failure by triggering re-authentication
   */
  private handleRefreshFailure(): void {
    console.log('🔴 Session refresh failed - user needs to re-authenticate')

    // Clear expired session data
    if (typeof window !== 'undefined') {
      localStorage.removeItem('ninx_session_expiry')
    }

    // You might want to redirect to login or show a notification
    // For now, we'll just log it - you can customize this behavior
    console.log(
      '💡 Consider implementing user notification for re-authentication'
    )
  }

  /**
   * Stop session refresh monitoring
   * Call this on logout or when no longer needed
   */
  public stopSessionRefresh(): void {
    console.log('🛑 Stopping session refresh monitoring')

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    this.sessionExpiryTime = null
    this.isRefreshing = false

    // Clear from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('ninx_session_expiry')
    }
  }

  /**
   * Check if session refresh is currently active
   */
  public isActive(): boolean {
    return this.refreshTimer !== null && this.sessionExpiryTime !== null
  }

  /**
   * Get time until next refresh (in milliseconds)
   */
  public getTimeUntilRefresh(): number | null {
    if (!this.sessionExpiryTime) return null
    return Math.max(
      0,
      this.sessionExpiryTime - Date.now() - REFRESH_LEAD_TIME_MS
    )
  }

  /**
   * Get session expiry time
   */
  public getSessionExpiryTime(): number | null {
    return this.sessionExpiryTime
  }

  /**
   * Manual refresh trigger (for testing or user-initiated refresh)
   */
  public async manualRefresh(): Promise<boolean> {
    try {
      await this.performRefresh()
      return true
    } catch (error) {
      console.error('Manual refresh failed:', error)
      return false
    }
  }
}

// Export singleton instance
export const sessionRefreshManager = SessionRefreshManager.getInstance()

// Helper function to start session refresh (convenience export)
export const startSessionRefresh = (expiresIn?: number) => {
  sessionRefreshManager.startSessionRefresh(expiresIn)
}

// Helper function to stop session refresh (convenience export)
export const stopSessionRefresh = () => {
  sessionRefreshManager.stopSessionRefresh()
}

// Helper function to resume session refresh on app load
export const resumeSessionRefresh = () => {
  sessionRefreshManager.resumeSessionRefresh()
}
